import React from 'react'
import { ArrowRight } from 'lucide-react'
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component'
import 'react-vertical-timeline-component/style.min.css'
import Hero from '../../assets/vision/Knowledge.jpg' 
import Header from "../../components/Navbar/Header"
import Footer from "../../components/Footer"
import { FaFlag, FaTelegram, FaTrain, FaPlane, FaBomb, FaRocket, FaDesktop, FaDna } from 'react-icons/fa'
import { CountUpStats } from './components/ui/KnowledgeCountUpStats'
import BackToVisionButton from './components/ui/BackToVisionButton'
import { Col, Container, Row } from 'react-bootstrap';

import ActionBtn from '../../components/Button/ActionBtn';

// Button component
const Button = ({ children, size = "default", variant = "default", className = "", ...props }) => {
  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
    icon: "h-10 w-10",
  }

  const variantClasses = {
    default: "bg-black text-white hover:bg-black/90",
    outline: "border border-black bg-white hover:bg-gray-100 text-black",
    secondary: "bg-gray-200 text-black hover:bg-gray-300",
    ghost: "hover:bg-gray-100 text-black",
    link: "text-black underline-offset-4 hover:underline",
  }

  return (
    <button
      className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

// TimelineItem component
const TimelineItem = ({ year, event, icon }) => (
  <VerticalTimelineElement
    className="vertical-timeline-element--work"
    contentStyle={{ background: 'rgb(255, 255, 255)', color: '#000' }}
    contentArrowStyle={{ borderRight: '7px solid  rgb(255, 255, 255)' }}
    date={year}
    iconStyle={{ background: 'rgb(33, 150, 243)', color: '#fff' }}
    icon={icon}
  >
    <p>{event}</p>
  </VerticalTimelineElement>
)

export default function Knowledge() {
  return (
    <div className="min-h-screen bg-white text-black">
      <Header className="fixed top-0 w-full z-10" initialTextColor="black" initialIconColor="black" forceIconColor="black" disableHoverEffect={true} />
      
      {/* Hero Section */}
      <section
        className="pt-32 pb-16 bg-cover bg-center text-white"
        style={{
          backgroundImage: `url(${Hero})`,
        }}
      >
      <div className="fixed top-20 left-4 z-40">
      <BackToVisionButton />
    </div>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl mb-6 text-black">
              Knowledge Is Power—And It's Yours to Command
            </h1>
            <p className="text-xl text-black mb-8">
              Coming Soon: The FuteurAI Knowledge Hub. Not just another blog. Not generic articles. The FuteurAI Knowledge Hub is your AI-powered guide to everything you need as a small business owner.
            </p>
            <Button size="lg" className="group">
              Be the First to Access
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-sm text-black mt-4">
              Empower yourself, shape your tomorrow, and know that anything is possible with the right knowledge.
            </p>
          </div>

          <CountUpStats />
        </div>
      </section>

      {/* Understanding the Challenges Section */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6 text-center">
              Understanding the Challenges
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-center">
              Small business owners face numerous obstacles, including:
            </p>
            <ul className="list-disc pl-6 space-y-4 text-gray-600 text-justify" >
              <li><strong>Limited Access to Capital:</strong> Securing funding remains a significant hurdle, often due to a lack of information on available resources and how to qualify for them.</li>
              <li><strong>Navigating Regulatory Landscapes:</strong> Keeping up with changing laws and regulations can be overwhelming without centralized, understandable resources.</li>
              <li><strong>Technological Advancements:</strong> Rapid tech evolution leaves many struggling to integrate new tools that could enhance efficiency and competitiveness.</li>
              <li><strong>Market Competition:</strong> Understanding market trends and consumer behavior is crucial yet challenging without access to relevant data and analysis.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-16 bg-gray-100">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6 text-center">
              Our Commitment to Bridging the Knowledge Gap
            </h2>
            <p className="text-lg text-gray-600 mb-8 text-center">
              Inspired by America's rich history of innovation and resilience, we are dedicated to honoring that legacy by empowering today's entrepreneurs. Our initiatives include:
            </p>
            <ul className="list-disc pl-6 space-y-4 text-gray-600 text-justify">
              <li><strong>Comprehensive Resource Library:</strong> Curated content covering finance, marketing, technology, and regulatory compliance, tailored to the needs of small businesses.</li>
              <li><strong>Interactive Learning Modules:</strong> Engaging tutorials and workshops designed to enhance your business acumen and technical skills.</li>
              <li><strong>Expert Insights:</strong> Access to industry experts and thought leaders who provide guidance on best practices and emerging trends.</li>
              <li><strong>Community Forums:</strong> A platform to connect with fellow entrepreneurs, share experiences, and build a supportive network.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* America's Legacy of Innovation Section */}
      <section className=" bg-cover bg-center text-black "style={{
          backgroundImage: `url(${Hero})`,
        }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6 p-5">
              America's Legacy of Innovation
            </h2>
            <p className="text-lg text-black mb-8">
              Through each milestone, America has led the way in reshaping industries, connecting people, and expanding the horizons of possibility. Each innovation serves as a testament to the pioneering spirit that defines our nation.
            </p>
          </div>
          <VerticalTimeline>
            <TimelineItem year="1790s" event="The U.S. Patent Act ignited a culture of innovation, supporting inventors like Eli Whitney and Thomas Edison." icon={<FaFlag />} />
            <TimelineItem year="1830s" event="Samuel Morse's electric telegraph transformed communication, bridging vast distances." icon={<FaTelegram />} />
            <TimelineItem year="1869" event="The completion of the Transcontinental Railroad linked coasts, accelerating commerce." icon={<FaTrain />} />
            <TimelineItem year="1903" event="The Wright brothers' first flight set humanity on a path to modern aviation." icon={<FaPlane />} />
            <TimelineItem year="1945" event="The Manhattan Project's development of the atomic bomb marked a turning point in history." icon={<FaBomb />} />
            <TimelineItem year="1969" event="NASA's Apollo 11 mission brought humanity to the moon." icon={<FaRocket />} />
            <TimelineItem year="1975" event="The advent of the personal computer began the Digital Age." icon={<FaDesktop />} />
            <TimelineItem year="2003" event="The Human Genome Project unveiled the secrets of our genetic code." icon={<FaDna />} />
          </VerticalTimeline>
        </div>
      </section>

      {/* Honoring Our Legacy Section */}
      <section className='py-12 sm:py-16 bg-white'>
      <Container className='px-4 sm:px-6 lg:px-8'>
        <div className="w-full bg-Mneutral100 rounded-[24px] sm:rounded-[56px] p-6 sm:p-8 lg:p-12">
        <Row className='gap-y-4'>
            <Col md={6} className='my-auto'>
            <video
              src="/Astronaut.mp4"
              className="rounded-[20px] w-full h-[600px] mb-10 object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
            </Col>
            <Col md={6} className='my-auto'>
            <h4 className='font-bold text-black text-3xl sm:text-4xl md:text-4xl mb-3'>Honoring Our Legacy, Shaping Our Future</h4>
             
              <p className='text__18 leading-relaxed text-Mneutral700 mb-4'>
              America's economic history is marked by milestones achieved through determination and ingenuity. From the industrial revolution to the digital age, each era has been propelled by individuals who dared to innovate. We honor this legacy by equipping you with the knowledge to drive your business forward, contributing to the nation's ongoing story of progress.
             
              </p>
            
            </Col>
          </Row>
        </div>
      </Container>
    </section>

      {/* Join Us Section */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl mb-6">
              Join Us in Empowering the Future
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              We invite you to explore our Knowledge Center, utilize the resources available, and become part of a community committed to excellence and growth. Together, we can overcome the challenges of today and build the successes of tomorrow, ensuring that the spirit of American enterprise continues to thrive.
            </p>
            <div className="space-y-4">
            <Button 
            size="lg" 
            variant="default" 
            className="group" 
            onClick={() => {
              window.location.href = 'https://app.futeur.ai/signup';
              setTimeout(() => window.scrollTo(0, 0), 0); // Ensure the scroll happens after navigation
            }}
          >
          Get Started With FuteurAI
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

